/**
 * LeaderboardRoute — Tavle-fanen i Premier, `/leaderboard` (Plan 02-16; #156,
 * steg 6d, Stell_Tavle).
 *
 * Fra toppen:
 *   - PremierFrame: ScreenHeader «Premier» med saldopillen til Lommebok, og
 *     underfanene rett under (`drawsOwnHeader` / `ownTabs`);
 *   - chipene «Denne uka» / «Totalt». De styres av tilstand, ikke ruter, og
 *     begge spørringene går ved montering, så byttet er umiddelbart;
 *   - lederkortet: pikselavataren (D2), «Leder uke N» eller «Flest poeng
 *     totalt», navnet, poengene og avstanden ned til nummer to;
 *   - radene med plass, avatar, poeng og en linje mot lederens poeng;
 *   - fotnoten om nullstilling og innløste poeng (leaderboard.service teller
 *     bare `amount > 0`).
 *
 * INV-LEAD-3 / LEAD-02: kronen står på lederkortet bare på ukevisningen.
 * Totalt-lederen er «tidenes», ikke ukens vinner, så der er det ingen krone.
 * Neste mandag 00:00 gir API-et kronen videre av seg selv (T-02-40).
 *
 * Tom tilstand: alle rader på 0 poeng (LEFT JOIN gir én rad per medlem, så
 * «ingen har tjent» er «alle på 0», ikke en tom liste).
 *
 * PersonalBestWeekCard (HIGH-1) står under fotnoten på ukevisningen. Den er
 * ikke i skissen, men er utjevneren for barn som sjelden leder, så den blir.
 *
 * Utelatt fra skissen: kosmetikken på lederens avatar. Andres utstyr er ny
 * oppførsel 8 (HANDOFF), så kortet viser pikselavataren uten lag.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, EmptyState, ErrorState, LoadingState } from '../../components/primitives';
import { CrownOverlay } from '../../components/primitives/CrownOverlay';
import { asColorSlot } from '../../components/primitives/InitialAvatar';
import { PixelAvatar } from '../../components/primitives/PixelAvatar';
import { formatPoints, formatWeekNumber } from '../../i18n/formatters';
import { useSession } from '../auth/useSession';
import { PersonalBestWeekCard } from '../points/PersonalBestWeekCard';
import { useBalance } from '../points/usePointsQueries';
import { BalancePill, PremierFrame } from '../rewards/PremierFrame';
import { LeaderboardRow } from './LeaderboardRow';
import {
  useAllTimeLeaderboard,
  useWeeklyLeaderboard,
  type LeaderRow,
} from './useLeaderboardQueries';

/** Innholdet holder seg til telefonbredden på nettbrett, som underfanene. */
const COLUMN = 'w-full md:max-w-xl';

function LeaderHero({
  leader,
  runnerUp,
  weekly,
}: {
  leader: LeaderRow;
  runnerUp: LeaderRow | undefined;
  weekly: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const points = formatPoints(leader.points);
  // Ved delt førsteplass står den andre lederen i teksten i stedet for
  // avstanden, så kortet ikke later som én av dem leder alene.
  let pointsLine = t('rewards.points', { points });
  if (runnerUp && runnerUp.points === leader.points) {
    pointsLine = t('leaderboard.hero.pointsTied', { points, name: runnerUp.short_name });
  } else if (runnerUp) {
    pointsLine = t('leaderboard.hero.pointsBehind', {
      points,
      name: runnerUp.short_name,
      gap: formatPoints(leader.points - runnerUp.points),
    });
  }
  return (
    <section
      data-testid="leaderboard-leader"
      aria-label={t('leaderboard.hero.label')}
      className={`${COLUMN} flex items-center gap-4 rounded-xl bg-panel p-4.5 text-on-panel`}
    >
      <div className="relative shrink-0">
        <span className="block size-18 overflow-hidden rounded-full border-4 border-white/70">
          <PixelAvatar colorSlot={asColorSlot(leader.slot)} size={64} />
        </span>
        {weekly ? <CrownOverlay label={t('leaderboard.hero.crown')} /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-control font-bold">
          {weekly
            ? t('leaderboard.hero.weekly', { week: formatWeekNumber(new Date()) })
            : t('leaderboard.hero.allTime')}
        </p>
        <p className="m-0 truncate text-heading-l font-extrabold tracking-tight">
          {leader.short_name}
        </p>
        <p data-testid="leaderboard-leader-points" className="tnum m-0 text-body-s font-bold">
          {pointsLine}
        </p>
      </div>
    </section>
  );
}

export function LeaderboardRoute(): ReactNode {
  const { t } = useTranslation();
  const [weekly, setWeekly] = useState(true);
  const session = useSession();
  const viewer = session.data?.member ?? null;
  const { data: balance } = useBalance(viewer?.id ?? '', viewer !== null);

  // Begge spørringene går ved montering (TanStack dedupliserer), så byttet
  // mellom chipene ikke viser et skjelett.
  const weeklyQuery = useWeeklyLeaderboard();
  const allTimeQuery = useAllTimeLeaderboard();
  const query = weekly ? weeklyQuery : allTimeQuery;
  const rows = query.data ?? [];

  const leader = rows[0];
  const isEmpty = !leader || !rows.some((r) => r.points > 0);

  let body: ReactNode;
  if (query.isError) {
    body = (
      <div data-testid="leaderboard-error" className={COLUMN}>
        <ErrorState
          variant="inline"
          heading={t('errors.generic.heading')}
          body={t('errors.generic.body')}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  } else if (query.isPending) {
    body = (
      <div data-testid="leaderboard-loading" className={COLUMN}>
        <LoadingState lines={4} />
      </div>
    );
  } else if (isEmpty) {
    body = (
      <div data-testid="leaderboard-empty" className={COLUMN}>
        <EmptyState
          variant="inline"
          heading={t('leaderboard.empty.heading')}
          body={t('leaderboard.empty.body')}
        />
      </div>
    );
  } else {
    body = (
      <>
        <LeaderHero leader={leader} runnerUp={rows[1]} weekly={weekly} />
        <ol
          data-testid="leaderboard-list"
          className={`${COLUMN} m-0 flex list-none flex-col gap-2 p-0`}
        >
          {rows.map((row) => (
            <LeaderboardRow
              key={row.memberId}
              row={row}
              top={leader.points}
              isCurrentViewer={viewer?.id === row.memberId}
            />
          ))}
        </ol>
      </>
    );
  }

  return (
    <PremierFrame
      testId="leaderboard-route"
      trailing={viewer ? <BalancePill balance={balance} data-testid="leaderboard-balance" /> : undefined}
    >
      <div role="group" aria-label={t('leaderboard.period')} className="flex gap-2">
        <Chip
          data-testid="leaderboard-tab-weekly"
          selected={weekly}
          onClick={() => setWeekly(true)}
        >
          {t('leaderboard.tabs.weekly')}
        </Chip>
        <Chip
          data-testid="leaderboard-tab-allTime"
          selected={!weekly}
          onClick={() => setWeekly(false)}
        >
          {t('leaderboard.tabs.allTime')}
        </Chip>
      </div>

      {body}

      <p
        data-testid="leaderboard-subline"
        className={`${COLUMN} m-0 text-center text-caption text-ink-2`}
      >
        {weekly ? t('leaderboard.foot.weekly') : t('leaderboard.foot.allTime')}
      </p>

      {/* Bare på ukevisningen og bare for den innloggede; kortet er null når
          medlemmet ikke har noen rekorduke ennå. */}
      {weekly && viewer ? (
        <PersonalBestWeekCard memberId={viewer.id} className={COLUMN} />
      ) : null}
    </PremierFrame>
  );
}
